import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { 
  MessageSquare, Phone, Mail, Calendar, AlertCircle, 
  Flag, Plus, Trash2, Pin, Edit, X, Check
} from 'lucide-react';
import { useAuth } from '@/context/AuthContext';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const NOTE_TYPES = [
  { value: 'call', label: 'Phone Call', icon: Phone, color: 'text-blue-500' },
  { value: 'email', label: 'Email', icon: Mail, color: 'text-purple-500' },
  { value: 'meeting', label: 'Meeting', icon: Calendar, color: 'text-green-500' },
  { value: 'issue', label: 'Issue/Problem', icon: AlertCircle, color: 'text-red-500' },
  { value: 'milestone', label: 'Milestone', icon: Flag, color: 'text-yellow-500' },
  { value: 'general', label: 'General Note', icon: MessageSquare, color: 'text-[#94a3b8]' }
];

const ProjectNotes = ({ projectId, projectName }) => {
  const { getAuthHeaders } = useAuth();
  const [notes, setNotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [editingNote, setEditingNote] = useState(null);
  
  const [newNote, setNewNote] = useState({
    note_type: 'general',
    content: '',
    contact_name: '',
    contact_phone: '',
    follow_up_date: '',
    is_pinned: false
  });

  useEffect(() => {
    if (projectId) {
      fetchNotes();
    }
  }, [projectId]);

  const fetchNotes = async () => {
    try {
      const response = await axios.get(`${API}/projects/${projectId}/notes`, {
        headers: getAuthHeaders()
      });
      setNotes(response.data);
    } catch (error) {
      console.error('Error fetching notes:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleAddNote = async () => {
    if (!newNote.content.trim()) {
      toast.error('Note content is required');
      return;
    }

    try {
      await axios.post(`${API}/projects/${projectId}/notes`, newNote, {
        headers: getAuthHeaders()
      });
      toast.success('Note added successfully!');
      setAddDialogOpen(false);
      resetForm();
      fetchNotes();
    } catch (error) {
      console.error('Error adding note:', error);
      toast.error('Failed to add note');
    }
  };

  const handleDeleteNote = async (noteId) => {
    if (!window.confirm('Delete this note?')) return;
    
    try {
      await axios.delete(`${API}/projects/${projectId}/notes/${noteId}`, {
        headers: getAuthHeaders()
      });
      toast.success('Note deleted');
      fetchNotes();
    } catch (error) {
      console.error('Error deleting note:', error);
      toast.error('Failed to delete note');
    }
  };

  const resetForm = () => {
    setNewNote({
      note_type: 'general',
      content: '',
      contact_name: '',
      contact_phone: '',
      follow_up_date: '',
      is_pinned: false
    });
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { 
      month: 'short', 
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit'
    });
  };

  const getNoteTypeInfo = (type) => {
    return NOTE_TYPES.find(t => t.value === type) || NOTE_TYPES[5]; // Default to general
  };

  // Sort notes: pinned first, then by date
  const sortedNotes = [...notes].sort((a, b) => {
    if (a.is_pinned && !b.is_pinned) return -1;
    if (!a.is_pinned && b.is_pinned) return 1;
    return new Date(b.created_at) - new Date(a.created_at);
  });

  return (
    <Card className="millmetrix-card p-6">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-bold text-white flex items-center gap-2">
          <MessageSquare className="w-5 h-5 text-[#f97316]" />
          Activity Log
        </h3>
        <Button
          size="sm"
          onClick={() => setAddDialogOpen(true)}
          className="bg-[#10b981] hover:bg-[#059669] text-white"
        >
          <Plus className="w-4 h-4 mr-1" />
          Add Note
        </Button>
      </div>

      {/* Notes List */}
      <div className="space-y-3 max-h-[400px] overflow-y-auto">
        {loading ? (
          <p className="text-[#94a3b8] text-center py-4">Loading notes...</p>
        ) : sortedNotes.length === 0 ? (
          <div className="text-center py-8">
            <MessageSquare className="w-12 h-12 text-[#94a3b8] mx-auto mb-2 opacity-50" />
            <p className="text-[#94a3b8]">No notes yet</p>
            <p className="text-[#64748b] text-sm">Add a note or call log to track activity</p>
          </div>
        ) : (
          sortedNotes.map((note) => {
            const typeInfo = getNoteTypeInfo(note.note_type);
            const IconComponent = typeInfo.icon;
            
            return (
              <div 
                key={note.id} 
                className={`p-4 bg-[#0f172a] rounded-lg border ${
                  note.is_pinned ? 'border-[#f97316]' : 'border-[rgba(255,255,255,0.05)]'
                }`}
              >
                <div className="flex items-start justify-between">
                  <div className="flex items-start gap-3 flex-1">
                    <IconComponent className={`w-5 h-5 ${typeInfo.color} flex-shrink-0 mt-0.5`} />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <span className={`text-xs font-semibold ${typeInfo.color}`}>
                          {typeInfo.label}
                        </span>
                        {note.is_pinned && (
                          <Pin className="w-3 h-3 text-[#f97316]" />
                        )}
                        {note.follow_up_date && (
                          <span className="text-xs bg-yellow-500/20 text-yellow-500 px-2 py-0.5 rounded">
                            Follow up: {new Date(note.follow_up_date).toLocaleDateString()}
                          </span>
                        )}
                      </div>
                      
                      <p className="text-white text-sm whitespace-pre-wrap">{note.content}</p>
                      
                      {(note.contact_name || note.contact_phone) && (
                        <div className="mt-2 flex items-center gap-3 text-xs text-[#94a3b8]">
                          {note.contact_name && (
                            <span className="flex items-center gap-1">
                              <Phone className="w-3 h-3" />
                              {note.contact_name}
                            </span>
                          )}
                          {note.contact_phone && (
                            <span>{note.contact_phone}</span>
                          )}
                        </div>
                      )}
                      
                      <div className="mt-2 flex items-center gap-2 text-xs text-[#64748b]">
                        <span>{note.created_by_name}</span>
                        <span>•</span>
                        <span>{formatDate(note.created_at)}</span>
                      </div>
                    </div>
                  </div>
                  
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handleDeleteNote(note.id)}
                    className="text-[#94a3b8] hover:text-red-500 flex-shrink-0"
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            );
          })
        )}
      </div>

      {/* Add Note Dialog */}
      <Dialog open={addDialogOpen} onOpenChange={setAddDialogOpen}>
        <DialogContent className="bg-[#1e293b] text-white border-[rgba(255,255,255,0.1)] max-w-lg">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold flex items-center gap-2">
              <Plus className="w-5 h-5 text-[#10b981]" />
              Add Note
            </DialogTitle>
            <DialogDescription className="text-[#94a3b8]">
              {projectName}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            {/* Note Type */}
            <div>
              <Label className="text-xs text-[#94a3b8] uppercase">Type</Label>
              <div className="grid grid-cols-3 gap-2 mt-2">
                {NOTE_TYPES.map(type => {
                  const Icon = type.icon;
                  return (
                    <button
                      key={type.value}
                      onClick={() => setNewNote({ ...newNote, note_type: type.value })}
                      className={`p-3 rounded-lg border flex flex-col items-center gap-1 transition-colors ${
                        newNote.note_type === type.value 
                          ? 'border-[#f97316] bg-[#f97316]/10' 
                          : 'border-[rgba(255,255,255,0.1)] hover:border-[#94a3b8]'
                      }`}
                    >
                      <Icon className={`w-5 h-5 ${type.color}`} />
                      <span className="text-xs text-[#94a3b8]">{type.label}</span>
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Content */}
            <div>
              <Label className="text-xs text-[#94a3b8] uppercase">Note *</Label>
              <textarea
                value={newNote.content}
                onChange={(e) => setNewNote({ ...newNote, content: e.target.value })}
                className="millmetrix-input mt-2 min-h-[100px] resize-none w-full"
                placeholder="Enter note details..."
                autoFocus
              />
            </div>

            {/* Contact Info (for calls/meetings) */}
            {['call', 'meeting', 'email'].includes(newNote.note_type) && (
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs text-[#94a3b8] uppercase">Contact Name</Label>
                  <Input
                    value={newNote.contact_name}
                    onChange={(e) => setNewNote({ ...newNote, contact_name: e.target.value })}
                    className="millmetrix-input mt-2"
                    placeholder="John Smith"
                  />
                </div>
                <div>
                  <Label className="text-xs text-[#94a3b8] uppercase">Phone</Label>
                  <Input
                    value={newNote.contact_phone}
                    onChange={(e) => setNewNote({ ...newNote, contact_phone: e.target.value })}
                    className="millmetrix-input mt-2"
                    placeholder="(555) 123-4567"
                  />
                </div>
              </div>
            )}

            {/* Follow Up Date */}
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs text-[#94a3b8] uppercase">Follow Up Date</Label>
                <Input
                  type="date"
                  value={newNote.follow_up_date}
                  onChange={(e) => setNewNote({ ...newNote, follow_up_date: e.target.value })}
                  className="millmetrix-input mt-2"
                />
              </div>
              <div className="flex items-end pb-2">
                <label className="flex items-center gap-2 text-sm text-[#94a3b8] cursor-pointer">
                  <input
                    type="checkbox"
                    checked={newNote.is_pinned}
                    onChange={(e) => setNewNote({ ...newNote, is_pinned: e.target.checked })}
                    className="w-4 h-4"
                  />
                  <Pin className="w-4 h-4" />
                  Pin to top
                </label>
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => { setAddDialogOpen(false); resetForm(); }}
              className="border-[#94a3b8] text-[#94a3b8]"
            >
              Cancel
            </Button>
            <Button
              onClick={handleAddNote}
              className="bg-[#10b981] hover:bg-[#059669] text-white"
            >
              Add Note
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
};

export default ProjectNotes;
